import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AudioPlayer, Badge } from "../../../components/ui";
import { useCentinelaCallRecording } from "../../../features/centinela/queries";
import { formatAgo } from "../services/formatWatchTime";
import type { CentinelaCallView } from "../services/mapCentinelaCall";
import { blockWaveform, waveformAllowed } from "../services/recordingWaveform";
import { CENTINELA_CALL_OUTCOME_TONES, CENTINELA_CALL_TYPE_ICONS } from "../theme";

export interface LastCallAudioProps {
  call: CentinelaCallView;
  tone: string;
}

export default function LastCallAudio({ call, tone }: LastCallAudioProps) {
  const { t } = useTranslation("common");

  const [armedId, setArmedId] = useState<string | null>(null);
  const [noWaveform, setNoWaveform] = useState(!waveformAllowed());

  const renewedFor = useRef<string | null>(null);

  const recordingQuery = useCentinelaCallRecording(armedId);
  const recording = recordingQuery.data?.recording ?? null;
  const audioUrl = recording?.disponible ? recording.url : null;
  const recordingMissing = Boolean(recording && !recording.disponible);
  const recordingFailed = recordingQuery.isError;

  const armRecording = () => {
    if (armedId === call.id) {
      void recordingQuery.refetch();
      return;
    }
    setArmedId(call.id);
  };

  useEffect(() => {
    setArmedId(null);
    setNoWaveform(!waveformAllowed());
    renewedFor.current = null;
  }, [call.id]);

  const dropWaveform = useCallback(() => {
    blockWaveform();
    setNoWaveform(true);
  }, []);

  const handleAudioError = () => {
    if (!armedId || renewedFor.current === armedId) return;
    renewedFor.current = armedId;
    void recordingQuery.refetch();
  };

  const minutes = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${`${seconds % 60}`.padStart(2, "0")}`;

  return (
    <div className="space-y-2 border-t border-line pt-3">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[9.5px] uppercase tracking-widest text-content-muted">
          {t("monitoreoIa.monitoringCenter.lastCall")}
        </span>
        <span className="ml-auto font-mono text-[9.5px] text-content-faint">
          {formatAgo(call.startedAt, t)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <i
          className={`${CENTINELA_CALL_TYPE_ICONS[call.callType] ?? "ri-phone-line"} text-sm text-primary-500`}
        />
        <span className="text-[11.5px] text-content">
          {t(`monitoreoIa.centinelaCalls.types.${call.callType}`, { defaultValue: call.callType })}
        </span>
        <Badge tone={CENTINELA_CALL_OUTCOME_TONES[call.outcome]} size="sm">
          {t(`monitoreoIa.centinelaCalls.outcomes.${call.outcome}`)}
        </Badge>
      </div>

      {call.findings.length > 0 ? (
        <p className="text-[10.5px] leading-snug text-content-secondary">
          {t(`monitoreoIa.centinelaCalls.findings.${call.findings[0].tipo}`, {
            defaultValue: call.findings[0].tipo,
          })}
          {call.findings[0].nota ? ` · ${call.findings[0].nota}` : ""}
        </p>
      ) : null}

      {!call.hasRecording ? (
        <p className="flex items-start gap-1.5 rounded-lg border border-warning/30 bg-warning/10 p-2 text-[10.5px] text-warning">
          <i className="ri-phone-line mt-0.5 shrink-0 text-xs" />
          {call.outcome === "noAnswer"
            ? t("monitoreoIa.monitoringCenter.noAnswerNoRecording")
            : t("monitoreoIa.calls.audioUnavailable")}
        </p>
      ) : (
        <>
          {!audioUrl ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={recordingQuery.isFetching}
                onClick={armRecording}
                title={t("monitoreoIa.calls.audioPlay")}
                aria-label={t("monitoreoIa.calls.audioPlay")}
                className="flex h-9 w-9 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-full border border-line bg-card text-primary-500 transition-colors hover:bg-subtle disabled:opacity-60"
              >
                <i
                  className={`text-xs ${
                    recordingQuery.isFetching ? "ri-loader-4-line animate-spin" : "ri-play-fill"
                  }`}
                />
              </button>

              <span className="shrink-0 font-mono text-[9.5px] max-sm:text-[11px] tabular-nums text-content-faint">
                {minutes(call.durationSeconds)}
              </span>
            </div>
          ) : null}

          {audioUrl ? (
            <AudioPlayer
              autoPlay
              src={audioUrl}
              duration={call.durationSeconds}
              waveform={!noWaveform}
              waveformTone={tone}
              playLabel={t("monitoreoIa.calls.audioPlay")}
              pauseLabel={t("monitoreoIa.calls.audioPause")}
              seekLabel={t("monitoreoIa.calls.audioSeek")}
              muteLabel={t("monitoreoIa.calls.audioMute")}
              unmuteLabel={t("monitoreoIa.calls.audioUnmute")}
              volumeLabel={t("monitoreoIa.calls.audioVolume")}
              onError={handleAudioError}
              onWaveformBlocked={dropWaveform}
            />
          ) : null}

          {recordingFailed ? (
            <p className="flex items-start gap-1.5 text-[10.5px] leading-snug text-error">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-xs" />
              {t("monitoreoIa.calls.audioFailed")}
            </p>
          ) : null}

          {recordingMissing ? (
            <p className="text-[10.5px] leading-snug text-content-faint">
              {t("monitoreoIa.calls.audioMissing")}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
